import { inboxSegmentHref } from "../messaging/inboxSegment";

export type Tab =
  | "home"
  | "requests"
  | "videos"
  | "submit"
  | "inbox"
  | "updates"
  | "script"
  | "brands";

/** A key into the `shell` catalogue, translated where it renders. */
export type TabTitleKey = `tabs.${Tab}.title`;
export type NavLabelKey = TabTitleKey | "tabs.home.nav";

/**
 * What each screen is called.
 *
 * The phone header carries the wordmark rather than the screen's name, so
 * these are the titles a screen prints in its own content, and the source the
 * rail's `NAV_LABELS` is built from.
 */
export const TAB_TITLES: Record<Tab, TabTitleKey> = {
  home: "tabs.home.title",
  requests: "tabs.requests.title",
  videos: "tabs.videos.title",
  submit: "tabs.submit.title",
  inbox: "tabs.inbox.title",
  updates: "tabs.updates.title",
  script: "tabs.script.title",
  brands: "tabs.brands.title",
};

/**
 * What navigation calls each destination, which is not always its title.
 *
 * Home's title is the product's name. In a rail the wordmark is already two
 * rows above it, so the item has to say what it is: Home.
 */
export const NAV_LABELS: Record<Tab, NavLabelKey> = {
  ...TAB_TITLES,
  home: "tabs.home.nav",
};

/**
 * Where each tab lives.
 *
 * Here rather than in `PortalShell` because the loading shell needs the same
 * answer: it has to know which rail item to mark active and which screen's
 * skeleton to draw, and a second copy of this map is a second place for a
 * route to be forgotten.
 *
 * Updates is a view inside Inbox, so its entry is that view's address and
 * neither the bar nor the rail offers it. It stays a `Tab` because the
 * prototype still draws it as one.
 *
 * Brands is the rail's one row the bar does not carry: the bar is full, and
 * on a phone the way in is the program sheet, beside the programs a brand
 * would join.
 */
export const TAB_PATH: Record<Tab, string> = {
  home: "/one",
  requests: "/one/requests",
  videos: "/one/videos",
  submit: "/one/submit",
  inbox: "/one/inbox",
  updates: inboxSegmentHref("updates"),
  script: "/one/script",
  brands: "/one/brands",
};

/** Home only when nothing else matches, so `/one/videos` is not also Home. */
export function tabForPath(pathname: string): Tab {
  return (
    Object.entries(TAB_PATH)
      .filter(([, path]) => path !== "/one" && pathname.startsWith(path))
      .map(([id]) => id as Tab)[0] ?? "home"
  );
}
